import {
  Box, Button, Text, useDisclosure, Link,
  Textarea,
  FormControl,
  FormErrorMessage
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from '@/api/backoffice-api';
import AppConfig from '@/utils/app-config.mjs';
import {
  getLicenseByState,
  getLicenseByZipDistance,
  getLicenseByZipQueryParams
} from '../ducks/selectors';
import { useNavigate } from 'react-router';
import { ControlNumberRecognizer, EINNumberRecognizer, FFLRecognizer, PhoneNumberRecognizer, Recognizer, ZipCodeRecognizer } from '@masterffl/ffl-search-v2-component';
import FFLMapView from './FFLMapView-v2';
import { setIsSearchStarted, setIsSearchingDealer, setSearchCount } from '../ducks/slice';
import { FFLDealerSearchGrid } from "@masterffl/ffl-grid-v2-component";
import CustomAddresses from '@/explore/ffl-detail/edit-license/components/CustomAddressesEdit';
import ModalComponent from '@/common/Modal';
import { getIsAssigningTransferDealer } from '@/queue/ducks/selectors';

interface Props {
  query: string;
  cognitoGroup: boolean;
  isSuperAdmin: boolean;
  searchRecognizer: Recognizer;
  selectedView: string;
  setNewPageNumber(pageNumber: number): void
  isFFLSelect?: boolean;
  onDealerAssigned?(arg: any, reason?:any): void;
  type?: string;
  isShowOnlyNFAFilterDisabled?: boolean;
  useSort?: string | null;
}
enum SortType {
  'Preferred' = 'preferred',
  'Distance' = 'distance',
  'Rating' = 'rating',
  'Name' = 'name'
}

type SortTypeKeyType = keyof typeof SortType;
const sortTypes = ['01', '02', '03', '06', '07', '08', '09', '10', '11'];

export default function DealerCardResults({
  query,
  cognitoGroup,
  isSuperAdmin,
  searchRecognizer,
  selectedView,
  setNewPageNumber,
  isFFLSelect,
  onDealerAssigned,
  type,
  isShowOnlyNFAFilterDisabled=false,
  useSort
}: Props) {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [componentData, setComponentData] = useState({
    nextPage: false,
    currentPageVal: 1,
    dealers: [],
    loadingData: false,
    errorData: false,
  });
  const [dealerData, setDealerData] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(20);
  const [searchString, setSearchString] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [isPageNumberOne, setIsPageNumberOne] = useState(false);
  const [containerHeight, setContainerHeight] = useState('');
  const getDistance = getLicenseByZipDistance();
  const getLicenseByZipParams = getLicenseByZipQueryParams();
  const params:any = { ...getLicenseByZipParams };
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedDealer, setSelectedDealer] = useState({
    dealerId: "",
    licenses: [],
  })
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [touched, setTouched] = useState(false);
  const distanceRef = useRef<string>('');
  const paramsRef = useRef<any>();
  const IsPageNumberOneRef = useRef<boolean>(false);
  const licenseByState = getLicenseByState();
  const licenseStatesRef = useRef<any[]>([]);

   const validateReason = (value: string) => {
    if (!value || value.trim().length === 0) {
      return 'Reason is required';
    }
    
    const trimmedValue = value.trim();
    
    // Check for minimum length
    if (trimmedValue.length < 5) {
      return 'Reason must be at least 5 characters long';
    }
    
    // Check for maximum length
    if (trimmedValue.length > 500) {
      return 'Reason cannot exceed 500 characters';
    }
    
    // Check if input contains only special characters or whitespace
    const onlySpecialCharsRegex = /^[^a-zA-Z0-9\s]*$/;
    if (onlySpecialCharsRegex.test(trimmedValue)) {
      return 'Reason must contain letters or numbers, not only special characters';
    }
    
    // Check if input contains only whitespace and special characters (no meaningful content)
    const noMeaningfulContentRegex = /^[\s\W]*$/;
    if (noMeaningfulContentRegex.test(trimmedValue)) {
      return 'Reason must contain meaningful text';
    }
    
    // Check for minimum alphanumeric characters (at least 3 letters/numbers)
    const alphanumericCount = (trimmedValue.match(/[a-zA-Z0-9]/g) || []).length;
    if (alphanumericCount < 3) {
      return 'Reason must contain at least 3 letters or numbers';
    }
    
    // Check for excessive repeated characters (more than 4 consecutive same characters)
    const repeatedCharsRegex = /(.)\1{4,}/;
    if (repeatedCharsRegex.test(trimmedValue)) {
      return 'Reason cannot contain more than 4 consecutive repeated characters';
    }
    
    // Check for prohibited characters or patterns
    const prohibitedPatternsRegex = /[<>{}[\]\\|`~]/;
    if (prohibitedPatternsRegex.test(trimmedValue)) {
      return 'Reason contains prohibited characters';
    }
    
    // Check if it's just repeated spaces with minimal content
    const wordCount = trimmedValue.split(/\s+/).filter(word => word.length > 0).length;
    if (wordCount < 2) {
      return 'Reason must contain at least 2 words';
    }
    
    return '';
  };

   const isFormValid = useMemo(() => {
    if (type === 'admin-approval') return true; // No validation needed for admin-approval
    return reason.trim().length > 0 && reasonError === '';
  }, [reason, reasonError, type]);

  if (params?.delivery_indicator && params?.delivery_indicator_commercial) {
    delete params.delivery_indicator;
    delete params.delivery_indicator_commercial;
  }

  if (params?.delivery_indicator || params?.delivery_indicator_commercial) {
    params.delivery_indicator = params?.delivery_indicator ? params?.delivery_indicator : params?.delivery_indicator_commercial;
    delete params?.delivery_indicator_commercial;
  }

  if(params?.state){
    delete params.state;
  }

  const getApiParams = useMemo(() => {
    let params = {}
    if (searchRecognizer instanceof ZipCodeRecognizer) params = { zipCode: query }
    else if (searchRecognizer instanceof FFLRecognizer) params = { license_key: query }
    else if (searchRecognizer instanceof ControlNumberRecognizer) params = { control_number: query }
    else if (searchRecognizer instanceof EINNumberRecognizer) params = { ein_number: query }
    else if (searchRecognizer instanceof PhoneNumberRecognizer) params = { phone_number: query }
    else params = { business_name: query }
    return params
  }, [searchRecognizer])

  const licenseStates = licenseByState && licenseByState.length > 0 && licenseByState.length !== 55? licenseByState?.join(',') : '';

  const [{ loading, error, data }] = useClient({
    baseURL: AppConfig.fflExpressServiceApiURL,
    url: `/dealers/search`,
    timeout: 60000,
    params: {
      ...params,
      types: params?.types
        ? params?.types
        : sortTypes.toString(),
      ...{ distance: getDistance, ...getApiParams, states: licenseStates },
      ...(isShowOnlyNFAFilterDisabled ? { exclude_non_sot_dealer: true } : { }),
      ...(useSort ? { useSort } : {}),
      page_size: pageSize,
      page_number: searchString !== query ? 1 : pageNumber
    }
  });
  
  useEffect(() => {
    setSearchString(query)
    if (searchString !== query && !isPageNumberOne) {
      setIsPageNumberOne(true)
      IsPageNumberOneRef.current = true;
      setPageNumber(1)
      const detailsData = {
        ...componentData,
        currentPageVal: 1,
      }
      setComponentData(detailsData)
    }
  }, [query])

  useEffect(() => {
    if (!loading) {
      setNewPageNumber(pageNumber);
    }
  }, [pageNumber, loading])

  useEffect(() => {
    const detailsData = {
      ...componentData,
      dealers: [],
      loadingData: loading,
    }
    setComponentData(detailsData)
    setDealerData([])
    setPageNumber(1)
    setIsPageNumberOne(true)
  }, [getLicenseByZipParams, query]);

  useEffect(() => {
    const detailsData = {
      ...componentData,
      errorData: false,
      loadingData: loading
    }
    if (loading) {
      dispatch(setIsSearchStarted(true));
    }else{
      IsPageNumberOneRef.current = false;
    }
    setComponentData(detailsData)
    dispatch(setIsSearchingDealer(true));
  }, [loading]);

  useEffect(() => {
    if (error) {
      clearData(true);
    }
    dispatch(setIsSearchingDealer(false));
  }, [error]);

  const clearData = (dataNotFound = false) => {
    const detailsData = {
      nextPage: false,
      currentPageVal: 1,
      dealers: [],
      loadingData: !dataNotFound,
      errorData: dataNotFound,
    }
    dispatch(setSearchCount(-1));
    if (dataNotFound) dispatch(setIsSearchStarted(false))
    setComponentData(detailsData)
    setDealerData([])
    setPageNumber(1)
  }

  useEffect(() => {
    IsPageNumberOneRef.current = false;
  }, [JSON.stringify(data)])
  

  useEffect(() => {
    if (data && data.data) {
      const dealerList = [...dealerData, ...data.data];
      const detailsData: any = {
        nextPage: data?.metadata?.hasMorePages ?? false,
        currentPageVal: pageNumber,
        dealers: dealerList,
        loadingData: false,
        errorData: false,
      }
      dispatch(setSearchCount(Number(data?.metadata?.totalRecords) || 0));
      dispatch(setIsSearchStarted(false))
      setDealerData(dealerList)
      setComponentData(detailsData)
    } else if (!error && !loading) {
      const detailsData: any = {
        nextPage: false,
        currentPageVal: pageNumber,
        dealers: [],
        loadingData: false,
        errorData: false,
      }
      dispatch(setSearchCount(-1));
      dispatch(setIsSearchStarted(false))
      setDealerData([])
      setComponentData(detailsData)
    }
    setIsPageNumberOne(false)
     if (IsPageNumberOneRef.current && pageNumber === 1) {
      IsPageNumberOneRef.current = false;
    }
    dispatch(setIsSearchingDealer(false));
  }, [data]);

  useEffect(() => {
    if(!loading){
      setIsPageNumberOne(false)
    }
  }, [error, loading]);

  useEffect(() => {
    if ((JSON.stringify(licenseByState) !== JSON.stringify(licenseStatesRef.current)) || (JSON.stringify(params) !== JSON.stringify(paramsRef.current))) {
  
      // Clear everything when distance changes
      setPageNumber(1);
      setDealerData([]);
      setComponentData({
        nextPage: false,
        currentPageVal: 1,
        dealers: [],
        loadingData: true,
        errorData: false,
      });
      setIsPageNumberOne(true);
      licenseStatesRef.current = licenseByState || [];
      IsPageNumberOneRef.current = true;
      paramsRef.current = params;
    }
  }, [licenseByState, params]);

  useEffect(() => {
  if ((getDistance !== distanceRef.current) || (JSON.stringify(params) !== JSON.stringify(paramsRef.current))) {

    // Clear everything when distance changes
    setPageNumber(1);
    setDealerData([]);
    setComponentData({
      nextPage: false,
      currentPageVal: 1,
      dealers: [],
      loadingData: true,
      errorData: false,
    });
    setIsPageNumberOne(true);
    distanceRef.current = getDistance;
    IsPageNumberOneRef.current = true;
    paramsRef.current = params;
  }
}, [getDistance, params]);

  const onFetchData = (args: any, isClearData = false) => {
    let currentPageSize = 0;
    let currentPageNumber = 0;
    setPageSize((currentValue) => {
      currentPageSize = currentValue
      return currentValue
    })
    setPageNumber((currentValue) => {
      currentPageNumber = currentValue
      return currentValue
    })
    if (isClearData && args.pageSize !== currentPageSize) {
      clearData()
    }
    if (args.nextPage != currentPageNumber && !IsPageNumberOneRef.current) {
      setPageNumber(args.nextPage == 0 ? 1 : args.nextPage)
    }
    if (args.pageSize !== currentPageSize) {
      setPageSize(args.pageSize)
    }
  }

  const onSelectCard = (dealerId: string) => {
    navigate(`/dealers-search/${dealerId}`);
  };

  useEffect(() => {
    function updateContainerHeight() {
      const element = document.querySelector('.search-custom-chip-box');

      const chipHeight = element && element.clientHeight ? element.clientHeight : 0;
      const notFound = document.getElementById('grid-component');
      const height = `calc(100vh - ${340 + chipHeight}px)`
      setContainerHeight(height)
      if (notFound) {
        notFound.style.minHeight = height
      }
    }

    updateContainerHeight();

    window.addEventListener('resize', updateContainerHeight);

    return () => {
      window.removeEventListener('resize', updateContainerHeight);
    };
  }, [getDistance, getLicenseByZipParams, query]);

  const onOpenHandler = (dealer: any) => {
    if (typeof (dealer) === 'object') {
      setSelectedDealer(dealer)
      setReason('')
      setReasonError('')
      setTouched(false)
      onOpen()
    }
  }
  const onAssignDealer = () => {
    if (onDealerAssigned) {
      onDealerAssigned(selectedDealer, reason)
    };
  }

  const handleReasonChange = (event: any) => {
    const value = event.target.value;
    setReason(value);
    
    // Only validate if the field has been touched or if there's a value
    if (touched || value) {
      const error = validateReason(value);
      setReasonError(error);
    }
  }

  const handleReasonBlur = () => {
    setTouched(true);
    const error = validateReason(reason);
    setReasonError(error);
  }

  
  const getCardActionButtons = useMemo(() => {
    if (isFFLSelect) {
    return [
      {
        label:  'Select',
        color: '#0c1e46',
        borderColor: '#0c1e46',
      },
    ];
  }
  return [];
  }, [isFFLSelect]);


  const getAssignDelerContent = (type: any) => {
    if (type === 'admin-approval') {
      return <>
        <Text>Do you want to select this license <Link color='teal.500' href="#"
          onClick={() => { if (selectedDealer.dealerId) window.open(`/dealers-search/${selectedDealer.dealerId}`, '_blank'); }}><Text as='u'>{formattedLicenseKey}</Text></Link> ?</Text>
      </>
    }
    return <>
      <Text>Do you want to reassign this transfer to dealer <Link color='teal.500' href="#"
        onClick={() => { if (selectedDealer.dealerId) window.open(`/dealers-search/${selectedDealer.dealerId}`, '_blank'); }}><Text as='u'>{formattedLicenseKey}</Text></Link> ?</Text>
     <FormControl isInvalid={touched && !!reasonError} sx={{ marginTop: '1rem' }}>
        <Textarea
          placeholder='Please provide a reason for reassignment (minimum 5 characters)'
          rows={5}
          value={reason}
          onChange={handleReasonChange}
          onBlur={handleReasonBlur}
          name='textarea'
          resize='vertical'
        />
        <FormErrorMessage>{reasonError}</FormErrorMessage>
      </FormControl>
    </>
  }

  const licenseKey = selectedDealer?.licenses.filter((license: any) => license.isPrimaryForInbound).map((license: any) => license.licenseKey)[0];
  const formattedLicenseKey = licenseKey ? `${licenseKey.slice(0, 1)}-${licenseKey.slice(1, 3)}-xxx-xx-xx-${licenseKey.slice(3)}` : "";
  const isReassignDealerInProgress = getIsAssigningTransferDealer();
  function displayResults() {
    const { sortBy, descending, ...rest } = getLicenseByZipParams
    const mediaQuery = window.matchMedia('(max-width: 743px)');
    return (
      <>
        <Box sx={{ display: selectedView == 'list' ? 'block' : 'none', height: '100%' }}>
          <>
            <div>
              <FFLDealerSearchGrid
                dataList={componentData}
                clickHandler={isFFLSelect ? onOpenHandler : onSelectCard}
                fetchData={onFetchData}
                maxHeight={null}
                licenseByZipParams={getLicenseByZipParams}
                btnTitle={isFFLSelect ? "Select" : null}
                isFFLSelect={isFFLSelect}
                actionButtons={getCardActionButtons || []}
                isCardV2={true}
              />
            </div>
          </>
        </Box>
        {selectedView === 'map' && (
          <>
            <FFLMapView
              query={query}
              cognitoGroup={cognitoGroup}
              isSuperAdmin={isSuperAdmin}
              searchRecognizer={searchRecognizer}
              containerHeight={containerHeight}
              useSort={useSort}
              isCardV2={true}
            />
          </>
        )}
        <ModalComponent
          footer={
            <>
              <Button
                colorScheme="blue"
                background="#ffffff"
                color="#0C1E46"
                border="1px solid #0C1E46"
                padding="8px 11px"
                marginRight="20px"
                onClick={() => {
                  onClose();
                }}
                width='80px'
              >
                No
              </Button>
              <Button
                colorScheme="blue"
                background="#0C1E46"
                padding="8px 20px"
                onClick={onAssignDealer}
                isLoading={isReassignDealerInProgress}
                isDisabled={!isFormValid || isReassignDealerInProgress}
                width='80px'
              >
                Yes
              </Button>
            </>
          }
          isOpen={isOpen}
          onClose={onClose}
          children={getAssignDelerContent(type)}
          title={type === 'admin-approval' ? 'CONFIRM THIS LICENSE' : 'CONFIRM THIS DEALER'}
          variant={2}
          maxWidth='650px'
        />
      </>
    );
  }
  function displayResultV2() {
    const { sortBy, descending, ...rest } = getLicenseByZipParams
    const mediaQuery = window.matchMedia('(max-width: 743px)');
    return (
      <>
        <Box sx={{ display: selectedView == 'list' ? 'block' : 'none', height: '100%' }}>
          <>
            <div>
              <FFLDealerSearchGrid
                dataList={componentData}
                clickHandler={isFFLSelect ? onOpenHandler : onSelectCard}
                fetchData={onFetchData}
                maxHeight={null}
                licenseByZipParams={getLicenseByZipParams}
                btnTitle={isFFLSelect ? "Select" : null}
                isCardV2={true}
                actionButtons={getCardActionButtons || []}
                isFFLSelect={isFFLSelect}
              />
            </div>
          </>
        </Box>
        {selectedView === 'map' && (
          <>
            <FFLMapView
              query={query}
              cognitoGroup={cognitoGroup}
              isSuperAdmin={isSuperAdmin}
              searchRecognizer={searchRecognizer}
              containerHeight={containerHeight}
              useSort={useSort}
              isCardV2={true}
            />
          </>
        )}
        <ModalComponent
          footer={
            <>
              <Button
                colorScheme="blue"
                background="#ffffff"
                color="#0C1E46"
                border="1px solid #0C1E46"
                padding="8px 11px"
                marginRight="20px"
                isDisabled={isReassignDealerInProgress}
                onClick={() => {
                  onClose();
                }}
                width='80px'
              >
                No
              </Button>
              <Button
                colorScheme="blue"
                background="#0C1E46"
                padding="8px 20px"
                onClick={onAssignDealer}
                isLoading={isReassignDealerInProgress}
                width='80px'
              >
                Yes
              </Button>
            </>
          }
          isOpen={isOpen}
          onClose={onClose}
          children={<>
            <Text>Do you want to assign this license <Link color='teal.500' href="#"
              onClick={() => { if (selectedDealer.dealerId) window.open(`/dealers-search/${selectedDealer.dealerId}`, '_blank'); }}><Text as='u'>{formattedLicenseKey}</Text></Link> ?</Text>
          </>
          }
          title='CONFIRM THIS LICENSE'
          variant={2}
          maxWidth='650px'
        />
      </>
    );
  }

  return (
    <Box id="result-container" className={'ZipCodeResults gird-result'}>
      {type === 'initial-transfer' ? displayResultV2() : displayResults()}
    </Box>
  );
}
